import { ArrowPathIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { type FC, useState } from "react";

import { Input, WYSIWYG } from "@fronterahealth/frontera-ui-components";

interface SuggestionCriteria {
  id: string;
  name: string;
  category: string;
  content: string;
}

interface SuggestionCriteriaFormProps {
  criteria: SuggestionCriteria;
  onNameSave?: (name: string) => Promise<void>;
  onContentSave: (content: string) => Promise<void>;
  onCategorySave: (category: string) => Promise<void>;
  readOnly?: boolean;
}

export const SuggestionCriteriaForm: FC<SuggestionCriteriaFormProps> = ({
  criteria,
  onNameSave,
  onContentSave,
  onCategorySave,
  readOnly = false,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const withSaving = async (fn: () => Promise<void>) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await fn();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {isSaving && (
        <span className="mb-2 flex items-center gap-1 text-sm text-gray-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Saving
        </span>
      )}

      {onNameSave && (
        <div className="mt-2">
          <Input
            name={`name-${criteria.id}`}
            label="Name"
            placeholder="Enter criteria name"
            defaultValue={criteria.name}
            disabled={isSaving}
            onBlur={(e) => {
              if (!isSaving && e.target.value !== criteria.name) {
                withSaving(() => onNameSave(e.target.value));
              }
            }}
          />
        </div>
      )}

      <div className="mt-3">
        <Input
          name={`category-${criteria.id}`}
          label="Category"
          placeholder="No category"
          defaultValue={criteria.category}
          displayOnlyMode={readOnly}
          disabled={isSaving}
          onBlur={(e) => {
            if (!readOnly && !isSaving && e.target.value !== criteria.category) {
              withSaving(() => onCategorySave(e.target.value));
            }
          }}
        />
      </div>

      <div className="mt-4">
        {readOnly ? (
          <>
            <div className={`relative ${!expanded ? "max-h-40 overflow-hidden" : ""}`}>
              <WYSIWYG
                title=""
                placeholder=""
                existingContent={criteria.content}
                displayOnlyMode
                onSave={async () => {}}
                isSaving={false}
              />
              {!expanded && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-linear-to-t from-white to-transparent" />
              )}
            </div>
            <button
              type="button"
              className="mt-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <>
                  <ChevronUpIcon className="h-4 w-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4" />
                  Expand
                </>
              )}
            </button>
          </>
        ) : (
          <WYSIWYG
            title=""
            placeholder=""
            existingContent={criteria.content}
            displayOnlyMode={false}
            onSave={async ({ content }) => withSaving(() => onContentSave(content))}
            isSaving={isSaving}
            autoSave
            showAutoSaveMessage={false}
          />
        )}
      </div>
    </div>
  );
};
